import { ConnectingLineHandler } from "./ConnectingLineHandler";
import { CellSelection } from "./selection/CellSelection";
import { DIAGRAM_EMIT_EVENTS } from "../../../diagram/modules/DiagramEvents";
import { DiagramEventEmitter } from "../../../diagram/modules/DiagramEventEmitter";
import { CellRetrievalUtils } from "../utils/CellRetrievalUtils";
import { ConnectorsUtils } from "../utils/ConnectorsUtils";
import { SelectionWrapper } from "./selection/SelectionWrapper";
import { AnchoredPluginsHandler } from "./AnchoredPluginsHandler";
import { getInstanceIDFromCanvasInstance } from "../../../diagram/diagram-assets/diagram-positioning-state";

/**
 * @description Handles the dragging of a plugin
 */
export class PluginDragHandler {
    dragging;

    grid;

    initialCell;

    pluginContainer;

    cellContent;

    lastHoveredCell;

    labelHandler;

    eventEmitter;

    cellRetrieval;

    connectorsUtils;

    contentUuidBackup = null;

    instanceId;

    constructor(grid, cell, labelHandler) {
        this.grid = grid;
        this.initialCell = cell;
        this.instanceId = getInstanceIDFromCanvasInstance(cell.ctx);
        this.eventEmitter = new DiagramEventEmitter(this.instanceId);
        this.cellRetrieval = new CellRetrievalUtils();
        this.connectorsUtils = new ConnectorsUtils();
        this.pluginContainer = cell.content.instance.container;
        this.cellContent = cell.content.instance.content;
        this.labelHandler = labelHandler || cell.labelHandler;
        this.contentUuidBackup = this.initialCell.getContentInstance().uuid;
    }

    /**
     * @description Handles the mouse move while a cell is being dragged
     * @param {*} mousePos - The current mouse pos
     * @param {*} temporary  Temporary grid config obj
     */
    mouseMove(mousePos, temporary) {
        if (mousePos) {
            const hoveredCellPos = this.cellRetrieval.getCellPosition(mousePos, temporary, this.grid);
            const hoveredCell = this.cellRetrieval.getCell(hoveredCellPos, this.grid);
            if (this.lastHoveredCell && hoveredCell !== this.lastHoveredCell) {
                this.lastHoveredCell.draw();
            }
            if (!hoveredCell) {
                return;
            }
            this.initialCell.draw();
            this.lastHoveredCell = hoveredCell;
        }
    }

    /**
     * @description Handles the dropCell event
     */
    dropCell(mousePos, temporary, grid, oldState) {
        if (grid) this.grid = grid;
        // let cellSelection = new CellSelection();
        const cellPos = this.cellRetrieval.getCellPosition(mousePos, temporary, grid);
        const cell = this.cellRetrieval.getCell(cellPos, grid);
        const contentInstance = this.initialCell.getContentInstance();
        if (cell && cell.isVoid() && contentInstance) {
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.INVALID_PLUGIN_MOVE, { DRAGGED_PLUGIN_ID: contentInstance.data.id, DRAGGED_PLUGIN_UUID: contentInstance.uuid });
            return;
        }
        if (cell && cell !== this.initialCell) {
            if (cell.hasContent()) {
                const contentInstance = this.initialCell.getContentInstance();
                const pluginUuid = contentInstance.uuid;
                const pluginID = contentInstance.data.id;
                // Emit invalid CellMove. Occupied Cell
                this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.INVALID_PLUGIN_MOVE, { DRAGGED_PLUGIN_ID: pluginID, DRAGGED_PLUGIN_UUID: pluginUuid });
                return;
            }
            const connectingLineHandler = new ConnectingLineHandler(this.instanceId);
            // updating esxisting connectors on the update cell
            const oldEndpoints = this.connectorsUtils.getPossibleEndpointsOfCell(this.initialCell);

            const connectorsPassingThroughDropCell = this.connectorsUtils.getConnectorsPassingThroughDropCell(connectingLineHandler, cell);
            const droppedPluginConnectors = this.connectorsUtils.getPluginConnectorsFromMovedCell(connectingLineHandler, this.initialCell.uuid, cell.uuid, oldEndpoints);
            let allAffectedCells = this.connectorsUtils.getAllAffectedPassthroughCellsByMove(connectorsPassingThroughDropCell, droppedPluginConnectors, connectingLineHandler);

            // Redraw the initial and current drop cell with true param so they dont regenerate their lines.
            const payload = {
                oldPosition: { x: this.initialCell.layout.x, y: this.initialCell.layout.y },
                newPosition: { x: cell.layout.x, y: cell.layout.y }
            };

            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.PLUGIN_MOVED, payload, true, !!oldState);

            this.labelHandler.updateLabelPosition(this.initialCell, cell, grid);
            cell.update({}, { selected: true });
            this.initialCell.contentData.uuid = this.contentUuidBackup;
            cell.setContent({ factory: this.initialCell.content.factory }, this.initialCell.contentData);
            cell.draw(true);
            const anchorHandler = new AnchoredPluginsHandler(this.instanceId);
            anchorHandler.updatePluginInstance(cell.getContentInstance());
            // let dataToUpdate = cell.getContentConfig(cell.content.config.content, cell.layout, cell.ctx);
            // cell.getContentInstance.update(dataToUpdate.options);

            const selectionWrapper = new SelectionWrapper(this.instanceId);
            selectionWrapper.setSelected("CELL", cellPos, { event: false, grid });
            // cellSelection.setSelectedCell(cellPos, false, grid);
            const newEndpoints = this.connectorsUtils.getPossibleEndpointsOfCell(cell);
            this.initialCell.update({}, { selected: false });
            this.initialCell.clearContent();
            this.initialCell.draw();
            if (oldState) {
                this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.MULTIPLE_ACTIONS_HAPPENED, [
                    { event: DIAGRAM_EMIT_EVENTS.PLUGIN_MOVED, oldState, payload }
                ], true, false);
            }

            // Recalculate connectors PATH passing through "drop position".  Set as ignoredLines those that havent been recalculated
            // Recalculate connectors PATH for the incoming and outgoing connectors for the moved cell. Set as ignoredLines those that havent been recalculated
            this.connectorsUtils.recalculateAllAffectedPaths(this.grid, connectorsPassingThroughDropCell, droppedPluginConnectors);

            // Update the path and passthrough cells for passing through "drop position" connectors.
            // Update the path and passthrough cells for incoming and outcoming connectors.
            //      Also update the TO or FROM cellUuuid on both the connector and the cells (incoming / outgoing property)
            // Remove past passed through cells.
            // Add new passed through cells.
            const newPassthroughCells = [];
            connectorsPassingThroughDropCell.pathfindingData.forEach((newData) => {
                const cells = connectingLineHandler.updateConnectorPathV2(
                    newData.uuid,
                    { path: newData.path, side: newData.points.end[0].side },
                    this.grid
                );
                newPassthroughCells.push(...cells);
            });
            droppedPluginConnectors.pathFindingData.incoming.forEach((newData) => {
                const cells = connectingLineHandler.updateConnectorPathV2(
                    newData.uuid,
                    { path: newData.path, side: newData.points.end[0].side },
                    this.grid,
                    null,
                    newData.to.uuid
                );
                newPassthroughCells.push(...cells);
            });
            droppedPluginConnectors.pathFindingData.outgoing.forEach((newData) => {
                const cells = connectingLineHandler.updateConnectorPathV2(
                    newData.uuid,
                    { path: newData.path, side: newData.points.end[0].side },
                    this.grid,
                    newData.from.uuid
                );
                newPassthroughCells.push(...cells);
            });
            allAffectedCells.push(...newPassthroughCells);
            allAffectedCells = allAffectedCells.filter((elem, index, arr) => arr.indexOf(elem) === index);
            // Redraw affected Cells.
            this.connectorsUtils.redrawAffectedCells(allAffectedCells, connectingLineHandler);
            // Also redraw the newly affected cells after the path change.Filter them to be unique.
        }
    }

    /**
     * @description cancels the draging
     */
    cancelDrag() {
        this.initialCell.draw();
    }
}
